import React, { useState, useEffect } from 'react';
import { AlertTriangle, Zap, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { createPageUrl } from '@/utils';

// Session-scoped key so dismiss persists across navigation but resets on new
// browser sessions and auto-clears when the balance recovers.
const DISMISS_KEY = 'nn_lcb_dismissed';

export default function LowCreditsBanner({ totalCredits, threshold = 50 }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Clear the dismiss flag when the balance recovers above the threshold so
  // the banner reappears if credits drop again later in the same session.
  useEffect(() => {
    if (totalCredits !== null && totalCredits !== undefined && totalCredits > threshold) {
      try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      setDismissed(false);
    }
  }, [totalCredits, threshold]);

  if (dismissed || totalCredits === null || totalCredits === undefined || totalCredits > threshold) return null;

  const isEmpty = totalCredits === 0;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className={`mx-4 mt-3 p-3 rounded-xl flex items-center gap-3 border ${
      isEmpty
        ? 'bg-red-500/10 border-red-500/30 text-red-300'
        : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
    }`}>
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <p className="text-xs font-medium flex-1">
        {isEmpty ? 'No credits left. Generation is paused.' : `Low credits: ${totalCredits} remaining.`}
      </p>
      <Link to={createPageUrl('Credits')}>
        <Button size="sm" className="h-6 text-xs bg-[#06B6D4] hover:bg-[#0891B2] px-2">
          <Zap className="w-3 h-3 mr-1" /> Buy
        </Button>
      </Link>
      <button onClick={handleDismiss} aria-label="Dismiss low credits warning" className="opacity-60 hover:opacity-100">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}